import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  IconButton,
  Progress,
  FormControl,
  FormLabel,
  Image,
  Center,
  Flex,
  Badge,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepDescription,
  StepSeparator,
  Container,
  Heading,
  SimpleGrid,
  Tag,
  TagLabel,
  TagCloseButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import { FiArrowLeft, FiX, FiPlus, FiPlay, FiUpload, FiChevronRight, FiChevronLeft, FiCheck, FiVideo, FiImage, FiFileText } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { uploadFileDirect, createContentDocumentDirect } from '../services/uploadService';
import { useNavigate } from 'react-router-dom';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';

interface Episode {
  id: string;
  title: string;
  description: string;
  videoFile: File | null;
  videoPreview: string | null;
  subtitleFile: File | null;
  subtitlePreview: string | null;
}

interface Season {
  id: string;
  title: string;
  description: string;
  posterFile: File | null;
  posterPreview: string | null;
  episodes: Episode[];
}

interface SeriesFormData {
  title: string;
  description: string;
  genre: string;
  genres: string[];
  contentRating: string;
  posterFile: File | null;
  posterPreview: string | null;
  trailerFile: File | null;
  trailerPreview: string | null;
  seasons: Season[];
}

const UploadSeriesMultiStep: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<SeriesFormData>({
    title: '',
    description: '',
    genre: '',
    genres: [],
    contentRating: 'G',
    posterFile: null,
    posterPreview: null,
    trailerFile: null,
    trailerPreview: null,
    seasons: []
  });
  
  const [expandedSeasons, setExpandedSeasons] = useState<Set<string>>(new Set());
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  const handleInputChange = (field: keyof SeriesFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre: string) => {
    const currentGenres = formData.genres;
    const isSelected = currentGenres.includes(genre);
    
    if (isSelected) {
      setFormData(prev => ({
        ...prev,
        genres: currentGenres.filter(g => g !== genre)
      }));
    } else if (currentGenres.length < 3) {
      setFormData(prev => ({
        ...prev,
        genres: [...currentGenres, genre]
      }));
    }
  };

  const handlePosterSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please select an image file for the poster.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: 'Please select a poster image smaller than 5MB.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        posterFile: file,
        posterPreview: URL.createObjectURL(file)
      }));
    }
  };

  const handlePosterRemove = () => {
    if (formData.posterPreview) {
      URL.revokeObjectURL(formData.posterPreview);
    }
    setFormData(prev => ({
      ...prev,
      posterFile: null,
      posterPreview: null
    }));
  };

  const handleTrailerSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please select a video file for the trailer.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: 'Please select a trailer video smaller than 100MB.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        trailerFile: file,
        trailerPreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleTrailerRemove = () => {
    if (formData.trailerPreview) {
      URL.revokeObjectURL(formData.trailerPreview);
    }
    setFormData(prev => ({
      ...prev,
      trailerFile: null,
      trailerPreview: null
    }));
  };

  const addSeason = () => {
    const newSeason: Season = {
      id: `season-${Date.now()}`,
      title: '',
      description: '',
      posterFile: null,
      posterPreview: null,
      episodes: []
    };
    setFormData(prev => ({
      ...prev,
      seasons: [...prev.seasons, newSeason]
    }));
    // Auto-expand new season
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      newSet.add(newSeason.id);
      return newSet;
    });
  };

  const toggleSeasonExpansion = (seasonId: string) => {
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(seasonId)) {
        newSet.delete(seasonId);
      } else {
        newSet.add(seasonId);
      }
      return newSet;
    });
  };

  const updateSeason = (seasonId: string, field: keyof Season, value: any) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.map(season =>
        season.id === seasonId ? { ...season, [field]: value } : season
      )
    }));
  };

  const removeSeason = (seasonId: string) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.filter(season => season.id !== seasonId)
    }));
  };

  const addEpisode = (seasonId: string) => {
    const newEpisode: Episode = {
      id: `episode-${Date.now()}`,
      title: '',
      description: '',
      videoFile: null,
      videoPreview: null,
      subtitleFile: null,
      subtitlePreview: null
    };
    
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.map(season =>
        season.id === seasonId 
          ? { ...season, episodes: [...season.episodes, newEpisode] }
          : season
      )
    }));
  };

  const updateEpisode = (seasonId: string, episodeId: string, field: keyof Episode, value: any) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.map(season =>
        season.id === seasonId
          ? {
              ...season,
              episodes: season.episodes.map(episode =>
                episode.id === episodeId ? { ...episode, [field]: value } : episode
              )
            }
          : season
      )
    }));
  };

  const removeEpisode = (seasonId: string, episodeId: string) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.map(season =>
        season.id === seasonId
          ? {
              ...season,
              episodes: season.episodes.filter(episode => episode.id !== episodeId)
            }
          : season
      )
    }));
  };

  const handleEpisodeVideoSelect = (seasonId: string, episodeId: string, file: File) => {
    if (!file.type.startsWith('video/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a video file.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: 'Please select a video file smaller than 500MB.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const videoPreview = URL.createObjectURL(file);
    updateEpisode(seasonId, episodeId, 'videoFile', file);
    updateEpisode(seasonId, episodeId, 'videoPreview', videoPreview);
  };

  const handleEpisodeVideoRemove = (seasonId: string, episodeId: string) => {
    const season = formData.seasons.find(s => s.id === seasonId);
    const episode = season?.episodes.find(e => e.id === episodeId);
    
    if (episode?.videoPreview) {
      URL.revokeObjectURL(episode.videoPreview);
    }
    
    updateEpisode(seasonId, episodeId, 'videoFile', null);
    updateEpisode(seasonId, episodeId, 'videoPreview', null);
  };

  const handleEpisodeSubtitleSelect = (seasonId: string, episodeId: string, file: File) => {
    if (!file.type.includes('text/') && !file.name.endsWith('.srt') && !file.name.endsWith('.vtt')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a subtitle file (.srt or .vtt).',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    updateEpisode(seasonId, episodeId, 'subtitleFile', file);
    updateEpisode(seasonId, episodeId, 'subtitlePreview', file.name);
  };

  const handleSeasonPosterSelect = (seasonId: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file for the season poster.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: 'Please select a poster image smaller than 5MB.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    updateSeason(seasonId, 'posterFile', file);
    updateSeason(seasonId, 'posterPreview', URL.createObjectURL(file));
  };

  const handleSeasonPosterRemove = (seasonId: string) => {
    const season = formData.seasons.find(s => s.id === seasonId);
    if (season?.posterPreview) {
      URL.revokeObjectURL(season.posterPreview);
    }
    updateSeason(seasonId, 'posterFile', null);
    updateSeason(seasonId, 'posterPreview', null);
  };

  // Step navigation functions
  const nextStep = () => {
    if (currentStep === 1) {
      // Validate step 1
      if (!formData.title.trim()) {
        toast({
          title: 'Title required',
          description: 'Please enter a series title.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (formData.genres.length === 0) {
        toast({
          title: 'Genres required',
          description: 'Please select at least one genre.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, 2));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const canProceedToStep2 = () => {
    return formData.title.trim() && formData.genres.length > 0;
  };

  const handleSubmit = async () => {
    if (!user?.uid) {
      toast({
        title: 'Please log in to upload content',
        description: 'You need to be logged in to upload a series.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.title.trim()) {
      toast({
        title: 'Title required',
        description: 'Please enter a series title.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.genres.length === 0) {
      toast({
        title: 'Genres required',
        description: 'Please select at least one genre.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.seasons.length === 0) {
      toast({
        title: 'Seasons required',
        description: 'Please add at least one season.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if all seasons have episodes
    const hasEmptySeasons = formData.seasons.some(season => season.episodes.length === 0);
    if (hasEmptySeasons) {
      toast({
        title: 'Episodes required',
        description: 'Each season must have at least one episode.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if all episodes have videos
    const hasEmptyEpisodes = formData.seasons.some(season =>
      season.episodes.some(episode => !episode.videoFile)
    );
    if (hasEmptyEpisodes) {
      toast({
        title: 'Video files required',
        description: 'Each episode must have a video file.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload poster
      let posterUrl = '';
      if (formData.posterFile) {
        posterUrl = await uploadFileDirect(`series/posters/${user.uid}`, formData.posterFile);
        setUploadProgress(5);
      }

      // Upload trailer
      let trailerUrl = '';
      if (formData.trailerFile) {
        trailerUrl = await uploadFileDirect(`series/trailers/${user.uid}`, formData.trailerFile);
        setUploadProgress(10);
      }

      // Create series document
      const seriesData = {
        title: formData.title,
        description: formData.description,
        category: 'series',
        visibility: 'public',
        rating: formData.contentRating,
        language: 'English',
        genres: formData.genres,
        tags: '',
        seasonNumber: '1',
        episodeNumber: '',
        chapterNumber: ''
      };

      const assetUrls: { [key: string]: string } = {};
      if (posterUrl) assetUrls.poster = posterUrl;
      if (trailerUrl) assetUrls.trailer = trailerUrl;

      const seriesId = await createContentDocumentDirect(user.uid, seriesData, assetUrls);
      setUploadProgress(20);

      // Upload seasons and episodes
      let totalProgress = 20;
      const progressIncrement = 70 / (formData.seasons.length * 2); // 70% for uploads
      
      // Prepare seasons metadata for series document
      const seasonsMetadata: Array<{
        seasonNumber: number;
        title: string;
        description: string;
        posterUrl?: string;
        episodes: Array<{
          episodeNumber: number;
          title: string;
          description: string;
          videoUrl: string;
          subtitleUrl?: string;
        }>;
      }> = [];

      for (let seasonIndex = 0; seasonIndex < formData.seasons.length; seasonIndex++) {
        const season = formData.seasons[seasonIndex];
        
        // Upload season poster
        let seasonPosterUrl = '';
        if (season.posterFile) {
          seasonPosterUrl = await uploadFileDirect(
            `series/${user.uid}/${seriesId}/seasons/${seasonIndex + 1}/poster`,
            season.posterFile
          );
        }
        
        const seasonEpisodes: Array<{
          episodeNumber: number;
          title: string;
          description: string;
          videoUrl: string;
          subtitleUrl?: string;
        }> = [];
        
        // Upload episodes for this season
        for (let episodeIndex = 0; episodeIndex < season.episodes.length; episodeIndex++) {
          const episode = season.episodes[episodeIndex];
          
          if (episode.videoFile) {
            const videoUrl = await uploadFileDirect(
              `series/${user.uid}/${seriesId}/seasons/${seasonIndex + 1}/episodes/${episodeIndex + 1}`,
              episode.videoFile
            );
            
            // Upload subtitle if exists
            let subtitleUrl = '';
            if (episode.subtitleFile) {
              subtitleUrl = await uploadFileDirect(
                `series/${user.uid}/${seriesId}/seasons/${seasonIndex + 1}/episodes/${episodeIndex + 1}/subtitles`,
                episode.subtitleFile
              );
            }
            
            // Create episode document
            const episodeData = {
              title: episode.title,
              description: episode.description,
              category: 'episode',
              visibility: 'public',
              rating: formData.contentRating,
              language: 'English',
              genres: formData.genres,
              tags: '',
              seasonNumber: (seasonIndex + 1).toString(),
              episodeNumber: (episodeIndex + 1).toString(),
              chapterNumber: '',
              parentSeriesId: seriesId
            };

            const episodeAssetUrls: { [key: string]: string } = {};
            episodeAssetUrls.videoFile = videoUrl;
            if (subtitleUrl) episodeAssetUrls.subtitles = subtitleUrl;

            await createContentDocumentDirect(user.uid, episodeData, episodeAssetUrls);
            
            // Store episode metadata for series document
            seasonEpisodes.push({
              episodeNumber: episodeIndex + 1,
              title: episode.title,
              description: episode.description,
              videoUrl: videoUrl,
              subtitleUrl: subtitleUrl || undefined
            });
          }
          
          totalProgress += progressIncrement;
          setUploadProgress(Math.min(totalProgress, 90));
        }
        
        // Store season metadata
        seasonsMetadata.push({
          seasonNumber: seasonIndex + 1,
          title: season.title,
          description: season.description,
          posterUrl: seasonPosterUrl || undefined,
          episodes: seasonEpisodes
        });
      }

      // Calculate total series duration from episodes
      let totalDuration = 0;
      for (const season of seasonsMetadata) {
        for (const episode of season.episodes) {
          // Get duration from video URL (this would need to be calculated during upload)
          // For now, we'll estimate based on typical episode length
          totalDuration += 45; // Default 45 minutes per episode
        }
      }

      // Update series document with seasons metadata and duration
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../lib/firebase');
      await updateDoc(doc(db, 'content', seriesId), {
        'assets.seasons': seasonsMetadata,
        'seasons': seasonsMetadata, // Also store at root level for easy access
        'duration': totalDuration,
        'totalEpisodes': seasonsMetadata.reduce((total, season) => total + season.episodes.length, 0),
        'totalSeasons': seasonsMetadata.length
      });

      setUploadProgress(100);
      toast({
        title: 'Series uploaded successfully!',
        description: 'Your series has been uploaded and is now available.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      navigate(`/content/${seriesId}`);
    } catch (error) {
      console.error('Error uploading series:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your series. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={{ base: 4, md: 6 }} borderBottom="1px solid" borderColor={borderColor}>
        <Container maxW="1200px">
          <Flex 
            justify="space-between" 
            align={{ base: "start", md: "center" }} 
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 0 }}
          >
            <HStack gap={4} align="start">
              <IconButton
                aria-label="Go back"
                icon={<IconWrapper icon={FiArrowLeft} size={20} />}
                onClick={() => navigate(-1)}
                variant="ghost"
                size={{ base: "sm", md: "md" }}
              />
              <VStack align="start" gap={1}>
                <Heading size={{ base: "md", md: "lg" }} color={textColor}>
                  Upload Series
                </Heading>
                <Text color={labelColor} fontSize={{ base: "xs", md: "sm" }}>
                  Create a new series with seasons and episodes
                </Text>
              </VStack>
            </HStack>
            
            {currentStep === 2 && (
              <Button
                colorScheme="purple"
                onClick={handleSubmit}
                isLoading={isUploading}
                loadingText="Uploading..."
                isDisabled={isUploading}
                rightIcon={<IconWrapper icon={FiCheck} size={16} />}
                size={{ base: "sm", md: "md" }}
                w={{ base: "full", md: "auto" }}
              >
                Upload Series
              </Button>
            )}
          </Flex>
        </Container>
      </Box>

      {/* Progress Bar */}
      {isUploading && (
        <Box w="100%" bg="gray.200" borderRadius="full" h="4px">
          <Box
            bg="purple.500"
            h="100%"
            borderRadius="full"
            transition="width 0.3s"
            w={`${uploadProgress}%`}
          />
        </Box>
      )}

      {/* Main Content */}
      <Container maxW="1200px" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
        <VStack gap={{ base: 6, md: 8 }} align="stretch">
          {/* Stepper */}
          <VStack gap={4} align="stretch">
            <Stepper index={currentStep - 1} colorScheme="purple" size={{ base: "sm", md: "lg" }}>
              <Step>
                <StepIndicator>
                  <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                </StepIndicator>
                <Box flexShrink="0" display={{ base: "none", md: "block" }}>
                  <StepTitle>Series Information</StepTitle>
                  <StepDescription>Basic details and metadata</StepDescription>
                </Box>
                <StepSeparator />
              </Step>
              <Step>
                <StepIndicator>
                  <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                </StepIndicator>
                <Box flexShrink="0" display={{ base: "none", md: "block" }}>
                  <StepTitle>Seasons & Episodes</StepTitle>
                  <StepDescription>Create seasons and add episodes</StepDescription>
                </Box>
              </Step>
            </Stepper>
            
            {/* Mobile Step Indicator */}
            <Box display={{ base: "block", md: "none" }} textAlign="center">
              <Text fontSize="sm" color={textColor} fontWeight="medium">
                Step {currentStep} of 2: {currentStep === 1 ? 'Series Information' : 'Seasons & Episodes'}
              </Text>
            </Box>
          </VStack>

          {/* Step Content */}
          {currentStep === 1 && (
            <Card bg={cardBg}>
              <CardHeader>
                <HStack gap={3}>
                  <IconWrapper icon={FiFileText} size={24} color="purple.500" />
                  <VStack align="start" gap={1}>
                    <Heading size="md" color={textColor}>
                      Series Information
                    </Heading>
                    <Text color={labelColor} fontSize="sm">
                      Provide basic details about your series
                    </Text>
                  </VStack>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack gap={6} align="stretch">
                  {/* Title */}
                  <FormControl>
                    <FormLabel color={labelColor} fontWeight="bold">
                      Series Title *
                    </FormLabel>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Enter series title"
                      bg={inputBg}
                      borderColor={borderColor}
                      size="lg"
                    />
                  </FormControl>

                  {/* Description */}
                  <FormControl>
                    <FormLabel color={labelColor} fontWeight="bold">
                      Description *
                    </FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Enter series description"
                      bg={inputBg}
                      borderColor={borderColor}
                      rows={4}
                      size="lg"
                    />
                  </FormControl>

                  {/* Genres */}
                  <FormControl>
                    <FormLabel color={labelColor} fontWeight="bold">
                      Genres * (Select up to 3)
                    </FormLabel>
                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} gap={2}>
                      {GENRES.map(genre => (
                        <Button
                          key={genre}
                          size="sm"
                          variant={formData.genres.includes(genre) ? 'solid' : 'outline'}
                          colorScheme="purple"
                          onClick={() => handleGenreToggle(genre)}
                          isDisabled={!formData.genres.includes(genre) && formData.genres.length >= 3}
                          fontSize={{ base: "xs", sm: "sm" }}
                          px={{ base: 2, sm: 3 }}
                        >
                          {genre}
                        </Button>
                      ))}
                    </SimpleGrid>
                    {formData.genres.length > 0 && (
                      <HStack gap={2} mt={2}>
                        <Text fontSize="sm" color={labelColor}>Selected:</Text>
                        {formData.genres.map(genre => (
                          <Tag key={genre} colorScheme="purple" size="sm">
                            <TagLabel>{genre}</TagLabel>
                            <TagCloseButton onClick={() => handleGenreToggle(genre)} />
                          </Tag>
                        ))}
                      </HStack>
                    )}
                  </FormControl>

                  {/* Content Rating */}
                  <FormControl>
                    <FormLabel color={labelColor} fontWeight="bold">
                      Content Rating
                    </FormLabel>
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={2}>
                      {CONTENT_RATINGS.map(rating => (
                        <Button
                          key={rating.value}
                          size="sm"
                          variant={formData.contentRating === rating.value ? 'solid' : 'outline'}
                          colorScheme="purple"
                          onClick={() => handleInputChange('contentRating', rating.value)}
                          textAlign="left"
                          justifyContent="flex-start"
                          h="auto"
                          minH="60px"
                          p={3}
                          whiteSpace="normal"
                          wordBreak="break-word"
                          w="full"
                        >
                          <VStack align="start" gap={0.5} w="full" spacing={0}>
                            <Text fontSize="sm" fontWeight="bold" noOfLines={1} w="full">
                              {rating.value}
                            </Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={3} w="full" lineHeight="1.2">
                              {rating.description}
                            </Text>
                          </VStack>
                        </Button>
                      ))}
                    </SimpleGrid>
                  </FormControl>

                  {/* Series Poster */}
                  <FormControl>
                    <FormLabel color={labelColor} fontWeight="bold">
                      Series Poster
                    </FormLabel>
                    <Box
                      border="2px dashed"
                      borderColor={formData.posterFile ? 'purple.300' : borderColor}
                      borderRadius="lg"
                      p={{ base: 4, md: 8 }}
                      textAlign="center"
                      bg={formData.posterFile ? 'purple.50' : bgColor}
                      _dark={{ bg: formData.posterFile ? 'purple.900' : 'gray.600' }}
                      cursor="pointer"
                      _hover={{ borderColor: 'purple.400' }}
                      transition="all 0.2s"
                      onClick={() => document.getElementById('poster-upload')?.click()}
                      minH={{ base: "200px", md: "250px" }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {formData.posterFile ? (
                        <VStack gap={4}>
                          <Image
                            src={formData.posterPreview || ''}
                            alt="Poster preview"
                            maxH="200px"
                            borderRadius="lg"
                            boxShadow="lg"
                          />
                          <HStack gap={2}>
                            <Text fontSize="md" color={textColor} fontWeight="medium">
                              {formData.posterFile.name}
                            </Text>
                            <IconButton
                              aria-label="Remove poster"
                              icon={<IconWrapper icon={FiX} size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePosterRemove();
                              }}
                            />
                          </HStack>
                          <Text fontSize="sm" color={labelColor}>
                            Click to change poster
                          </Text>
                        </VStack>
                      ) : (
                        <VStack gap={3}>
                          <IconWrapper icon={FiImage} size={48} color="gray.400" />
                          <VStack gap={1}>
                            <Text fontSize="lg" color={textColor} fontWeight="medium">
                              Click to upload poster
                            </Text>
                            <Text fontSize="sm" color={labelColor}>
                              PNG, JPG up to 5MB
                            </Text>
                          </VStack>
                        </VStack>
                      )}
                    </Box>
                    <Input
                      id="poster-upload"
                      type="file"
                      accept="image/*"
                      onChange={handlePosterSelect}
                      display="none"
                    />
                  </FormControl>

                  {/* Series Trailer */}
                  <FormControl>
                    <FormLabel color={labelColor} fontWeight="bold">
                      Series Trailer (Optional)
                    </FormLabel>
                    <Box
                      border="2px dashed"
                      borderColor={formData.trailerFile ? 'purple.300' : borderColor}
                      borderRadius="lg"
                      p={{ base: 4, md: 8 }}
                      textAlign="center"
                      bg={formData.trailerFile ? 'purple.50' : bgColor}
                      _dark={{ bg: formData.trailerFile ? 'purple.900' : 'gray.600' }}
                      cursor="pointer"
                      _hover={{ borderColor: 'purple.400' }}
                      transition="all 0.2s"
                      onClick={() => document.getElementById('trailer-upload')?.click()}
                      minH={{ base: "200px", md: "250px" }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {formData.trailerFile ? (
                        <VStack gap={4}>
                          <Box position="relative">
                            <video
                              src={formData.trailerPreview || ''}
                              style={{ maxHeight: '200px', borderRadius: '12px' }}
                              controls
                            />
                            <Center
                              position="absolute"
                              top="50%"
                              left="50%"
                              transform="translate(-50%, -50%)"
                              bg="blackAlpha.600"
                              borderRadius="full"
                              p={3}
                            >
                              <IconWrapper icon={FiPlay} size={32} color="white" />
                            </Center>
                          </Box>
                          <HStack gap={2}>
                            <Text fontSize="md" color={textColor} fontWeight="medium">
                              {formData.trailerFile.name}
                            </Text>
                            <IconButton
                              aria-label="Remove trailer"
                              icon={<IconWrapper icon={FiX} size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTrailerRemove();
                              }}
                            />
                          </HStack>
                          <Text fontSize="sm" color={labelColor}>
                            Click to change trailer
                          </Text>
                        </VStack>
                      ) : (
                        <VStack gap={3}>
                          <IconWrapper icon={FiVideo} size={48} color="gray.400" />
                          <VStack gap={1}>
                            <Text fontSize="lg" color={textColor} fontWeight="medium">
                              Click to upload trailer
                            </Text>
                            <Text fontSize="sm" color={labelColor}>
                              MP4, MOV up to 100MB
                            </Text>
                          </VStack>
                        </VStack>
                      )}
                    </Box>
                    <Input
                      id="trailer-upload"
                      type="file"
                      accept="video/*"
                      onChange={handleTrailerSelect}
                      display="none"
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
          )}

          {currentStep === 2 && (
            <Card bg={cardBg}>
              <CardHeader>
                <VStack gap={4} align="stretch">
                  <HStack gap={3}>
                    <IconWrapper icon={FiVideo} size={24} color="purple.500" />
                    <VStack align="start" gap={1}>
                      <Heading size="md" color={textColor}>
                        Seasons & Episodes
                      </Heading>
                      <Text color={labelColor} fontSize="sm">
                        Create seasons and add episodes with videos
                      </Text>
                    </VStack>
                  </HStack>
                  <Button
                    leftIcon={<IconWrapper icon={FiPlus} size={16} />}
                    colorScheme="purple"
                    onClick={addSeason}
                    size={{ base: "sm", md: "md" }}
                    w={{ base: "full", md: "auto" }}
                    alignSelf={{ base: "stretch", md: "flex-end" }}
                  >
                    Add Season
                  </Button>
                </VStack>
              </CardHeader>
              <CardBody>
                <VStack gap={6} align="stretch">
                  {formData.seasons.length === 0 ? (
                    <Center py={16} color={labelColor}>
                      <VStack gap={4}>
                        <IconWrapper icon={FiVideo} size={64} color="gray.400" />
                        <VStack gap={2}>
                          <Text fontSize="xl" fontWeight="medium">
                            No seasons added yet
                          </Text>
                          <Text fontSize="sm" textAlign="center">
                            Click "Add Season" to start creating your series
                          </Text>
                        </VStack>
                      </VStack>
                    </Center>
                  ) : (
                    formData.seasons.map((season, seasonIndex) => (
                      <Box key={season.id} border="1px solid" borderColor={borderColor} borderRadius="lg" p={6}>
                        <VStack gap={6} align="stretch">
                          {/* Season Header */}
                          <Flex 
                            justify="space-between" 
                            align={{ base: "start", md: "center" }} 
                            direction={{ base: "column", md: "row" }}
                            gap={{ base: 3, md: 0 }}
                          >
                            <HStack gap={3} align="start" flex="1">
                              <Badge colorScheme="purple" variant="subtle" size={{ base: "md", md: "lg" }}>
                                Season {seasonIndex + 1}
                              </Badge>
                              <VStack align="start" gap={1} flex="1">
                                <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color={textColor} noOfLines={2}>
                                  {season.title || 'Untitled Season'}
                                </Text>
                                {season.description && (
                                  <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor} noOfLines={2}>
                                    {season.description}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                            <HStack gap={2} alignSelf={{ base: "flex-end", md: "center" }}>
                              <IconButton
                                aria-label="Toggle season"
                                icon={<IconWrapper icon={expandedSeasons.has(season.id) ? FiX : FiPlus} size={16} />}
                                size={{ base: "sm", md: "sm" }}
                                variant="ghost"
                                onClick={() => toggleSeasonExpansion(season.id)}
                              />
                              <IconButton
                                aria-label="Remove season"
                                icon={<IconWrapper icon={FiX} size={16} />}
                                size={{ base: "sm", md: "sm" }}
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => removeSeason(season.id)}
                              />
                            </HStack>
                          </Flex>

                          {/* Season Details */}
                          {expandedSeasons.has(season.id) && (
                            <VStack gap={6} align="stretch">
                              {/* Season Information */}
                              <VStack gap={4} align="stretch">
                                <VStack gap={4} align="stretch" flex="1">
                                  <Input
                                    placeholder="Season title"
                                    value={season.title}
                                    onChange={(e) => updateSeason(season.id, 'title', e.target.value)}
                                    bg={inputBg}
                                    borderColor={borderColor}
                                    size={{ base: "md", md: "lg" }}
                                  />
                                  <Textarea
                                    placeholder="Season description"
                                    value={season.description}
                                    onChange={(e) => updateSeason(season.id, 'description', e.target.value)}
                                    bg={inputBg}
                                    borderColor={borderColor}
                                    rows={3}
                                    size={{ base: "md", md: "lg" }}
                                  />
                                </VStack>
                                
                                {/* Season Poster */}
                                <Box
                                  border="2px dashed"
                                  borderColor={season.posterFile ? 'purple.300' : borderColor}
                                  borderRadius="md"
                                  p={{ base: 3, md: 4 }}
                                  textAlign="center"
                                  bg={season.posterFile ? 'purple.50' : bgColor}
                                  _dark={{ bg: season.posterFile ? 'purple.900' : 'gray.600' }}
                                  cursor="pointer"
                                  _hover={{ borderColor: 'purple.400' }}
                                  transition="all 0.2s"
                                  onClick={() => document.getElementById(`season-poster-${season.id}`)?.click()}
                                  minH={{ base: "100px", md: "120px" }}
                                  w={{ base: "full", md: "150px" }}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  {season.posterFile ? (
                                    <VStack gap={2}>
                                      <Image
                                        src={season.posterPreview || ''}
                                        alt="Season poster preview"
                                        maxH={{ base: "60px", md: "80px" }}
                                        borderRadius="md"
                                      />
                                      <Text fontSize={{ base: "2xs", md: "xs" }} color={textColor} fontWeight="medium" noOfLines={1}>
                                        {season.posterFile.name}
                                      </Text>
                                      <IconButton
                                        aria-label="Remove season poster"
                                        icon={<IconWrapper icon={FiX} size={12} />}
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSeasonPosterRemove(season.id);
                                        }}
                                      />
                                    </VStack>
                                  ) : (
                                    <VStack gap={1}>
                                      <IconWrapper icon={FiImage} size={24} color="gray.400" />
                                      <Text fontSize={{ base: "2xs", md: "xs" }} color={textColor}>
                                        Season Poster
                                      </Text>
                                    </VStack>
                                  )}
                                </Box>
                                <Input
                                  id={`season-poster-${season.id}`}
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleSeasonPosterSelect(season.id, file);
                                    }
                                  }}
                                  display="none"
                                />
                              </VStack>

                              {/* Episodes Section */}
                              <Divider />
                              <VStack gap={4} align="stretch">
                                <Flex 
                                  justify="space-between" 
                                  align="center" 
                                  direction={{ base: "column", sm: "row" }}
                                  gap={{ base: 3, sm: 0 }}
                                >
                                  <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color={textColor}>
                                    Episodes ({season.episodes.length})
                                  </Text>
                                  <Button
                                    leftIcon={<IconWrapper icon={FiPlus} size={16} />}
                                    colorScheme="purple"
                                    variant="outline"
                                    size={{ base: "sm", md: "sm" }}
                                    onClick={() => addEpisode(season.id)}
                                    w={{ base: "full", sm: "auto" }}
                                  >
                                    Add Episode
                                  </Button>
                                </Flex>

                                {season.episodes.length === 0 ? (
                                  <Center py={8} color={labelColor} border="1px dashed" borderColor={borderColor} borderRadius="md">
                                    <VStack gap={2}>
                                      <IconWrapper icon={FiVideo} size={32} color="gray.400" />
                                      <Text fontSize="sm">
                                        No episodes added yet
                                      </Text>
                                    </VStack>
                                  </Center>
                                ) : (
                                  <VStack gap={4} align="stretch">
                                    {season.episodes.map((episode, episodeIndex) => (
                                      <Box key={episode.id} border="1px solid" borderColor={borderColor} borderRadius="md" p={4}>
                                        <VStack gap={4} align="stretch">
                                          <Flex 
                                            justify="space-between" 
                                            align={{ base: "start", sm: "center" }} 
                                            direction={{ base: "column", sm: "row" }}
                                            gap={{ base: 2, sm: 0 }}
                                          >
                                            <HStack gap={3} align="start" flex="1">
                                              <Badge colorScheme="blue" variant="subtle" size={{ base: "sm", md: "md" }}>
                                                Episode {episodeIndex + 1}
                                              </Badge>
                                              <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium" color={textColor} noOfLines={2}>
                                                {episode.title || 'Untitled Episode'}
                                              </Text>
                                            </HStack>
                                            <IconButton
                                              aria-label="Remove episode"
                                              icon={<IconWrapper icon={FiX} size={16} />}
                                              size={{ base: "sm", md: "sm" }}
                                              variant="ghost"
                                              colorScheme="red"
                                              onClick={() => removeEpisode(season.id, episode.id)}
                                              alignSelf={{ base: "flex-end", sm: "center" }}
                                            />
                                          </Flex>

                                          <VStack gap={3} align="stretch">
                                            <Input
                                              placeholder="Episode title"
                                              value={episode.title}
                                              onChange={(e) => updateEpisode(season.id, episode.id, 'title', e.target.value)}
                                              bg={inputBg}
                                              borderColor={borderColor}
                                              size={{ base: "sm", md: "md" }}
                                            />
                                            <Textarea
                                              placeholder="Episode description"
                                              value={episode.description}
                                              onChange={(e) => updateEpisode(season.id, episode.id, 'description', e.target.value)}
                                              bg={inputBg}
                                              borderColor={borderColor}
                                              rows={2}
                                              size={{ base: "sm", md: "md" }}
                                            />
                                          </VStack>

                                          {/* Video Upload */}
                                          <Box
                                            border="2px dashed"
                                            borderColor={episode.videoFile ? 'purple.300' : borderColor}
                                            borderRadius="md"
                                            p={{ base: 4, md: 6 }}
                                            textAlign="center"
                                            bg={episode.videoFile ? 'purple.50' : bgColor}
                                            _dark={{ bg: episode.videoFile ? 'purple.900' : 'gray.600' }}
                                            cursor="pointer"
                                            _hover={{ borderColor: 'purple.400' }}
                                            transition="all 0.2s"
                                            onClick={() => document.getElementById(`video-upload-${episode.id}`)?.click()}
                                            minH={{ base: "100px", md: "120px" }}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                          >
                                            {episode.videoFile ? (
                                              <VStack gap={{ base: 2, md: 3 }}>
                                                <HStack gap={2} flexWrap="wrap" justify="center">
                                                  <IconWrapper icon={FiVideo} size={20} color="purple.500" />
                                                  <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontWeight="medium" noOfLines={1}>
                                                    {episode.videoFile.name}
                                                  </Text>
                                                </HStack>
                                                <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor}>
                                                  Click to change video
                                                </Text>
                                              </VStack>
                                            ) : (
                                              <VStack gap={{ base: 2, md: 2 }}>
                                                <IconWrapper icon={FiUpload} size={32} color="gray.400" />
                                                <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontWeight="medium">
                                                  Click to upload video
                                                </Text>
                                                <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor}>
                                                  MP4, MOV up to 500MB
                                                </Text>
                                              </VStack>
                                            )}
                                          </Box>
                                          <Input
                                            id={`video-upload-${episode.id}`}
                                            type="file"
                                            accept="video/*"
                                            onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) {
                                                handleEpisodeVideoSelect(season.id, episode.id, file);
                                              }
                                            }}
                                            display="none"
                                          />

                                          {/* Subtitle Upload */}
                                          <Box
                                            border="1px solid"
                                            borderColor={episode.subtitleFile ? 'purple.300' : borderColor}
                                            borderRadius="md"
                                            p={{ base: 3, md: 4 }}
                                            textAlign="center"
                                            bg={episode.subtitleFile ? 'purple.50' : bgColor}
                                            _dark={{ bg: episode.subtitleFile ? 'purple.900' : 'gray.600' }}
                                            cursor="pointer"
                                            _hover={{ borderColor: 'purple.400' }}
                                            transition="all 0.2s"
                                            onClick={() => document.getElementById(`subtitle-upload-${episode.id}`)?.click()}
                                            minH="60px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                          >
                                            {episode.subtitleFile ? (
                                              <HStack gap={2} flexWrap="wrap" justify="center">
                                                <IconWrapper icon={FiFileText} size={16} color="purple.500" />
                                                <Text fontSize={{ base: "xs", md: "sm" }} color={textColor} fontWeight="medium" noOfLines={1}>
                                                  {episode.subtitlePreview}
                                                </Text>
                                                <IconButton
                                                  aria-label="Remove subtitle"
                                                  icon={<IconWrapper icon={FiX} size={12} />}
                                                  size="xs"
                                                  variant="ghost"
                                                  colorScheme="red"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateEpisode(season.id, episode.id, 'subtitleFile', null);
                                                    updateEpisode(season.id, episode.id, 'subtitlePreview', null);
                                                  }}
                                                />
                                              </HStack>
                                            ) : (
                                              <VStack gap={1}>
                                                <IconWrapper icon={FiFileText} size={20} color="gray.400" />
                                                <Text fontSize={{ base: "xs", md: "sm" }} color={textColor}>
                                                  Add subtitles (Optional)
                                                </Text>
                                                <Text fontSize={{ base: "2xs", md: "xs" }} color={labelColor}>
                                                  .srt or .vtt files
                                                </Text>
                                              </VStack>
                                            )}
                                          </Box>
                                          <Input
                                            id={`subtitle-upload-${episode.id}`}
                                            type="file"
                                            accept=".srt,.vtt,text/*"
                                            onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) {
                                                handleEpisodeSubtitleSelect(season.id, episode.id, file);
                                              }
                                            }}
                                            display="none"
                                          />
                                        </VStack>
                                      </Box>
                                    ))}
                                  </VStack>
                                )}
                              </VStack>
                            </VStack>
                          )}
                        </VStack>
                      </Box>
                    ))
                  )}
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Step Navigation */}
          <Flex 
            justify="space-between" 
            align="center" 
            pt={{ base: 4, md: 6 }} 
            gap={{ base: 3, md: 4 }} 
            direction={{ base: "column", md: "row" }}
            w="full"
          >
            <Button
              leftIcon={<IconWrapper icon={FiChevronLeft} size={16} />}
              variant="outline"
              onClick={prevStep}
              isDisabled={currentStep === 1}
              size={{ base: "sm", md: "md" }}
              minW={{ base: "full", md: "120px" }}
              order={{ base: 2, md: 1 }}
              h={{ base: "40px", md: "auto" }}
            >
              Previous
            </Button>
            
            {currentStep === 1 ? (
              <Button
                rightIcon={<IconWrapper icon={FiChevronRight} size={16} />}
                colorScheme="purple"
                onClick={nextStep}
                isDisabled={!canProceedToStep2()}
                size={{ base: "sm", md: "md" }}
                minW={{ base: "full", md: "200px" }}
                order={{ base: 1, md: 2 }}
                h={{ base: "40px", md: "auto" }}
              >
                <Text display={{ base: "none", sm: "inline" }}>Next: Seasons & Episodes</Text>
                <Text display={{ base: "inline", sm: "none" }}>Next</Text>
              </Button>
            ) : (
              <Box flex="1" order={{ base: 1, md: 2 }} maxW={{ base: "100%", md: "500px" }}>
                <Alert status="info" borderRadius="md" w="full" size={{ base: "sm", md: "md" }}>
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize={{ base: "xs", md: "sm" }}>Ready to upload?</AlertTitle>
                    <AlertDescription fontSize={{ base: "2xs", md: "xs" }}>
                      Make sure all episodes have video files before uploading.
                    </AlertDescription>
                  </Box>
                </Alert>
              </Box>
            )}
          </Flex>
        </VStack>
      </Container>
    </Box>
  );
};

export default UploadSeriesMultiStep;
